import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { OperatorSelect } from "@/components/Employees";
import { DateInput } from "@/components/DateInput";
import { num, materialsTotal, partsTotal, buildMaterials, buildParts } from "@/lib/consuntivo";
import { MaterialEditor, PartsEditor } from "@/components/ConsuntivoEditors";
import { toast } from "sonner";
import { Check, Plus, Trash2, Loader2 } from "lucide-react";

const big = "w-full h-12 rounded-xl bg-[#181A22] border border-white/15 px-3 text-base text-white";
const fmt = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "");
const today = () => new Date().toLocaleDateString("sv-SE");
const H3 = ({ children }) => <h3 className="text-sm font-bold uppercase tracking-wider text-[#E63946]">{children}</h3>;

const EntryCard = ({ e, i, onEdit, testid }) => (
  <div className={`rounded-2xl border border-white/10 bg-[#181A22]/60 p-3 space-y-2 ${e.removed ? "opacity-40" : ""}`} data-testid={`${testid}-${i}`}>
    <div className="flex items-center gap-2">
      {e.ts_id ? <span className="text-xs text-muted-foreground mr-auto">{fmt(e.date)}</span>
        : <DateInput value={e.date} onChange={(ev) => onEdit(i, "date", ev.target.value)} className="mr-auto h-10 bg-[#181A22] border-white/15 text-white" data-testid={`${testid}-${i}-date`} />}
      <button type="button" onClick={() => onEdit(i, "removed", !e.removed)} className="h-10 px-3 rounded-full border border-white/15 text-xs text-muted-foreground flex items-center gap-1" data-testid={`${testid}-${i}-remove`}>
        <Trash2 className="w-4 h-4" />{e.removed ? "Ripristina" : "Togli"}
      </button>
    </div>
    <div className="grid grid-cols-[1fr_90px] gap-2">
      <OperatorSelect value={e.operator} onChange={(v) => onEdit(i, "operator", v)} className={big} testid={`${testid}-${i}-operator`} />
      <input value={e.hours} onChange={(ev) => onEdit(i, "hours", ev.target.value)} inputMode="decimal" placeholder="Ore" className={`${big} text-right font-bold`} aria-label="Ore" data-testid={`${testid}-${i}-hours`} />
    </div>
    <input value={e.works} onChange={(ev) => onEdit(i, "works", ev.target.value)} placeholder="Lavoro eseguito" className={big} data-testid={`${testid}-${i}-works`} />
  </div>
);

const useHoursMaterials = (open, id) => {
  const [d, setD] = useState(null);
  const [entries, setEntries] = useState([]);
  const [added, setAdded] = useState([]);
  const [mats, setMats] = useState([]);
  const [matsDirty, setMatsDirty] = useState(false);
  const [parts, setPartsRaw] = useState([]);
  useEffect(() => {
    if (!open || !id) return;
    setD(null);
    api.get(`/admin/bodyshop/${id}/consuntivo`).then(({ data }) => {
      setD(data);
      setEntries(data.hours.entries.map((e) => ({ ...e, hours: String(e.hours).replace(".", ","), orig: e })));
      setAdded([]);
      setMats(buildMaterials(data));
      setPartsRaw(buildParts(data));
      setMatsDirty(false);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open, id]);
  const editEntry = (i, k, v) => setEntries((es) => es.map((e, j) => (j === i || (k === "operator" && e.ts_id === es[i].ts_id) ? { ...e, [k]: v } : e)));
  const editAdded = (i, k, v) => setAdded((as) => (k === "removed" ? as.filter((_, j) => j !== i) : as.map((e, j) => (j === i ? { ...e, [k]: v } : e))));
  const addRow = () => setAdded((as) => [...as, { operator: "", date: today(), works: "", hours: "" }]);
  const setMaterials = (m) => { setMats(m); setMatsDirty(true); };
  const setParts = (p) => { setPartsRaw(p); setMatsDirty(true); };
  return { d, entries, added, mats, parts, matsDirty, editEntry, editAdded, addRow, setMaterials, setParts };
};

const saveAll = async (id, s) => {
  const { d, entries, added, mats, parts, matsDirty } = s;
  const plate = d.job.plate;
  const fixes = entries.filter((e) => e.removed || e.operator !== e.orig.operator || e.works !== e.orig.works || num(e.hours) !== e.orig.hours);
  if ([...fixes, ...added].some((e) => !e.removed && !String(e.operator).trim())) throw new Error("Scegli il dipendente per ogni riga di ore");
  if (added.some((e) => !num(e.hours))) throw new Error("Scrivi le ore per ogni nuova riga");
  if (fixes.length) await api.post("/admin/timesheets/fix-entries", { entries: fixes.map((e) => ({ ts_id: e.ts_id, row: e.row, operator: e.operator || e.orig.operator, works: e.works, hours: num(e.hours), plate, delete: !!e.removed })) });
  for (const e of added) {
    await api.post("/admin/timesheets", { operator: e.operator, date: e.date, rows: [{ vehicle: `${d.job.brand} ${d.job.model}`.trim(), plate, works: e.works, hours: num(e.hours) }] });
  }
  if (matsDirty) {
    await api.put(`/admin/bodyshop/${id}/consuntivo`, { hourly_rate: d.final.hourly_rate, notes: d.final.notes || "", materials: mats, materials_amount: materialsTotal(mats), parts, parts_amount: partsTotal(parts) });
  }
};

export const HoursMaterialsSheet = ({ open, onOpenChange, id, onSaved }) => {
  const s = useHoursMaterials(open, id);
  const [busy, setBusy] = useState(false);
  const closed = s.d?.job.status === "closed";
  const save = async () => {
    setBusy(true);
    try {
      await saveAll(id, s);
      toast.success("Ore e materiali salvati: consuntivo aggiornato");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e.response ? formatApiErrorDetail(e.response.data?.detail) : e.message); }
    finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg bg-[#12141A] border-white/10 p-0 flex flex-col [&>button]:top-3 [&>button]:right-3 [&>button]:p-3 [&>button]:rounded-full [&>button>svg]:w-5 [&>button>svg]:h-5" data-testid="hours-materials-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-white/10">
          <SheetTitle className="font-display uppercase text-left">Ore dipendenti, materiali e ricambi</SheetTitle>
        </SheetHeader>
        {!s.d ? <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#E63946]" /></div> : closed ? (
          <p className="p-5 text-sm text-muted-foreground" data-testid="hm-closed">Il lavoro è segnato come finito e le ore sono bloccate. Tocca <b>Modifica → Riapri</b> per correggerle.</p>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">
              <section className="space-y-3">
                <H3>Ore dipendenti</H3>
                {s.entries.length + s.added.length === 0 && <p className="text-sm text-muted-foreground">Nessuna ora caricata per la targa {s.d.job.plate || "—"}.</p>}
                {s.entries.map((e, i) => <EntryCard key={`${e.ts_id}-${e.row}`} e={e} i={i} onEdit={s.editEntry} testid="hm-entry" />)}
                {s.added.map((e, i) => <EntryCard key={`n${i}`} e={e} i={i} onEdit={s.editAdded} testid="hm-new" />)}
                <Button type="button" variant="outline" onClick={s.addRow} className="w-full h-12 rounded-full border-dashed border-white/25 bg-transparent" data-testid="hm-add-hours"><Plus className="w-4 h-4 mr-1.5" />Aggiungi ore</Button>
              </section>
              <section className="space-y-3">
                <H3>Materiali</H3>
                <MaterialEditor rows={s.mats} set={s.setMaterials} />
              </section>
              <section className="space-y-3">
                <H3>Ricambi</H3>
                <PartsEditor rows={s.parts} set={s.setParts} />
              </section>
              <p className="text-xs text-muted-foreground">Dopo il salvataggio, scheda, consuntivo e ore del mese del dipendente si aggiornano da soli.</p>
            </div>
            <div className="border-t border-white/10 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <Button onClick={save} disabled={busy} className="w-full h-12 rounded-full bg-[#E63946] hover:bg-[#D62828] text-white text-base" data-testid="hm-save-btn">
                {busy ? <Loader2 className="w-5 h-5 animate-spin mr-1.5" /> : <Check className="w-5 h-5 mr-1.5" />}Salva ore e materiali
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
